/* ============================================================================
   qm-pdp-lite.js — Quirky Moose · produktsiden (2026-09-04)
   Indlæses fra snippets/qm-pdp-assets.liquid (inventory-blokken i begge
   produktskabeloner).

   1) Prisen i købsknappen ([data-qm-atc-price]) opdateres ved variantskift fra
      den HTML, Dawns product-info.js allerede har hentet. AKTIV.

   2) Dynamisk fragtpris under lagerstatus. IKKE AKTIV endnu (besluttet 4/9-2026):
      koden ligger klar, men gør intet, så længe snippets/qm-shipping-line ikke
      renderes i skabelonerne. Aktivering ved en kommende temaopdatering:
        a) sæt shop-metafeltet custom.storefront_api_token (Storefront API-token)
        b) tilføj {% render 'qm-shipping-line', product: product, section_id: section.id %}
           i inventory-blokken i templates/product.json og
           templates/product.product-with-video.json
      Virkemåde: opretter en midlertidig kurv via Storefront API (cartCreate) med
      den valgte variant og en leveringsadresse (landekode, evt. + et
      repræsentativt postnummer som andet forsøg) og læser
      deliveryGroups.deliveryOptions.estimatedCost — Shopifys egen beregning
      (Shipmondo-satserne), så tallet er det samme som i checkout.
      · Land: markedets land, medmindre Shopifys geolokation peger på et andet
        land i samme valuta (data fra #QmSuggestData, som forslagskortet henter).
      · Cache pr. land+variant i sessionStorage (1 time).
      · Ingen token, fejl eller ingen leveringsmuligheder → linjen forbliver skjult.

   ROLLBACK: fjern script-linjen i snippets/qm-pdp-assets.liquid.
   ========================================================================= */
(() => {
  const CACHE_TTL_MS = 60 * 60 * 1000;
  const CACHE_PREFIX = 'qm_ship:';

  const lineEl = () => document.querySelector('[data-qm-shipping]');

  const cacheGet = (key) => {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.v;
    } catch (e) {
      return null;
    }
  };
  const cacheSet = (key, value) => {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
    } catch (e) {
      /* privat vindue m.m. — så spørger vi bare igen */
    }
  };

  const formatMoney = (amount, currency, locale) => {
    const n = Number(amount);
    const whole = Math.abs(n - Math.round(n)) < 0.005;
    try {
      return new Intl.NumberFormat(locale || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch (e) {
      return `${n} ${currency}`;
    }
  };

  const countryName = (code, locale) => {
    try {
      return new Intl.DisplayNames([locale || 'en'], { type: 'region' }).of(code) || code;
    } catch (e) {
      return code;
    }
  };

  /* Landet, vi spørger for: markedets land — eller det geolokaliserede land,
     hvis forslagskortets data siger, at det handler i samme valuta. */
  let detectedCountry = null;
  const resolveCountry = (el) => {
    const market = el.dataset.country;
    if (!detectedCountry || detectedCountry === market) return market;
    try {
      const data = JSON.parse(document.getElementById('QmSuggestData').textContent);
      const target = data.countries && data.countries[detectedCountry];
      if (target && target.cur === el.dataset.currency) return detectedCountry;
    } catch (e) {
      /* ingen forslagsdata på siden */
    }
    return market;
  };

  const detectCountry = (el) =>
    new Promise((resolve) => {
      let root = el.dataset.root || '/';
      if (root.charAt(root.length - 1) !== '/') root += '/';
      fetch(root + 'browsing_context_suggestions.json?country[enabled]=true', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          const cc = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
          resolve(cc ? String(cc).toUpperCase() : null);
        })
        .catch(() => resolve(null));
      setTimeout(() => resolve(null), 2500);
    });

  const QUERY = `mutation qmRates($input: CartInput!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    cartCreate(input: $input) {
      cart { deliveryGroups(first: 5) { nodes { deliveryOptions { handle title code estimatedCost { amount currencyCode } } } } }
      userErrors { field message }
    }
  }`;

  /* Et repræsentativt postnummer pr. forsendelsesland. Bruges KUN som andet forsøg,
     hvis Shopify ikke giver leveringsmuligheder ud fra landekoden alene — nogle
     carrier-tjenester vil have et postnummer, før de svarer. Satserne er pr. land,
     så postnummeret ændrer ikke prisen. */
  const SAMPLE_ZIP = {
    DK: '1050', SE: '111 22', NO: '0150', FI: '00100', DE: '10115', AT: '1010', CH: '8001',
    NL: '1012 AB', BE: '1000', LU: '1009', FR: '75001', ES: '28001', PT: '1000-001', IT: '00100',
    IE: 'D01 F5P2', GB: 'SW1A 1AA', PL: '00-001', CZ: '110 00', SK: '811 01', HU: '1011',
    SI: '1000', HR: '10000', RO: '010011', BG: '1000', GR: '105 57', EE: '10111', LV: 'LV-1050',
    LT: '01100', MT: 'VLT 1110', AU: '2000',
  };

  const buildInput = (country, variantId, zip) => {
    const deliveryAddress = { countryCode: country };
    if (zip) deliveryAddress.zip = zip;
    return {
      lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
      buyerIdentity: { countryCode: country },
      delivery: {
        addresses: [
          {
            address: { deliveryAddress },
            selected: true,
            oneTimeUse: true,
            validationStrategy: 'COUNTRY_CODE_ONLY',
          },
        ],
      },
    };
  };

  const callStorefront = (el, country, input) =>
    fetch(el.dataset.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': el.dataset.token },
      body: JSON.stringify({ query: QUERY, variables: { country, language: el.dataset.language || 'EN', input } }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const cart = j && j.data && j.data.cartCreate && j.data.cartCreate.cart;
        if (!cart) return null;
        const options = [];
        (cart.deliveryGroups.nodes || []).forEach((g) => (g.deliveryOptions || []).forEach((o) => options.push(o)));
        if (!options.length) return null;
        let cheapest = null;
        options.forEach((o) => {
          const amount = Number(o.estimatedCost.amount);
          if (cheapest === null || amount < cheapest.amount) cheapest = { amount, currency: o.estimatedCost.currencyCode };
        });
        return { cheapest, count: options.length };
      })
      .catch(() => null);

  const fetchRates = (el, country, variantId) => {
    if (!el.dataset.token) return Promise.resolve(null);
    return callStorefront(el, country, buildInput(country, variantId)).then((result) => {
      if (result || !SAMPLE_ZIP[country]) return result;
      return callStorefront(el, country, buildInput(country, variantId, SAMPLE_ZIP[country]));
    });
  };

  const render = (el, country, result) => {
    const textEl = el.querySelector('[data-qm-shipping-text]');
    if (!textEl) return;
    if (!result || !result.cheapest) {
      el.hidden = true;
      textEl.textContent = '';
      return;
    }
    const locale = el.dataset.locale || 'en';
    const name = countryName(country, locale);
    let template;
    if (result.cheapest.amount <= 0) template = el.dataset.textFree;
    else if (result.count > 1) template = el.dataset.textFrom;
    else template = el.dataset.textPrice;
    const price = formatMoney(result.cheapest.amount, result.cheapest.currency, locale);
    textEl.textContent = (template || '').replace('__COUNTRY__', name).replace('__PRICE__', price);
    el.hidden = false;
  };

  let requestSeq = 0;
  const update = (variantId) => {
    const el = lineEl();
    if (!el || !el.dataset.token) return;
    const id = variantId || el.dataset.variantId;
    if (!id) return;
    el.dataset.variantId = id;
    const country = resolveCountry(el);
    const key = `${country}:${id}`;
    const seq = ++requestSeq;
    const cached = cacheGet(key);
    if (cached) {
      render(el, country, cached);
      return;
    }
    fetchRates(el, country, id).then((result) => {
      if (seq !== requestSeq) return; // et nyere variantskift har overhalet
      if (result) cacheSet(key, result);
      render(el, country, result);
    });
  };

  /* Pris i købsknappen — kopieres fra den sektions-HTML Dawn har hentet. */
  const syncButtonPrice = (html, sectionId) => {
    if (!html || !sectionId) return;
    const button = document.getElementById(`ProductSubmitButton-${sectionId}`);
    if (!button) return;
    const source = html.querySelector('[data-qm-atc-price]');
    let target = button.querySelector('[data-qm-atc-price]');
    if (source) {
      if (!target) {
        target = document.createElement('span');
        target.className = 'qm-atc-price';
        target.setAttribute('data-qm-atc-price', '');
        target.setAttribute('aria-hidden', 'true');
        button.insertBefore(target, button.querySelector('.loading__spinner'));
      }
      target.textContent = source.textContent;
    } else if (target) {
      target.remove();
    }
  };

  const hook = () => {
    // PUB_SUB_EVENTS er en top-level const i Dawns constants.js — ikke en window-egenskab.
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event && event.data ? event.data : null;
      if (!data) return;
      syncButtonPrice(data.html, data.sectionId);
      if (data.variant && data.variant.id) update(data.variant.id);
    });
    return true;
  };

  const boot = () => {
    if (!hook()) document.addEventListener('DOMContentLoaded', hook, { once: true });
    const el = lineEl();
    if (!el || !el.dataset.token) return;
    detectCountry(el).then((cc) => {
      detectedCountry = cc;
      update();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
